import React, { useCallback, useEffect, useState } from 'react';
import { Guide, Button, IconClose } from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import useLocale from '@/hooks/useLocale';
import * as Styles from './VpsGuidedTour.styles';
import { vpsOciActions } from '@/redux/modules';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

const VpsGuidedTour = ({
  myPlanRef,
  vpsInfoRef,
  sshAccessRef,
  managementRef,
  onClose,
  shouldStart,
  isReviewingTour,
  saveTourDismissed,
  tourSession,
}) => {
  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 960;
  const isTablet = width < 1200;

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);

  const dispatch = useDispatch();

  const { id: hostingId } = useParams();

  const handleClose = useCallback(() => {
    const isLastStep = currentStep === steps.length - 1;
    !isReviewingTour && !isLastStep && saveTourDismissed();

    setSteps([]);
    setCurrentStep(0);
    onClose && onClose(currentStep);
  }, [onClose, currentStep, steps.length, isReviewingTour, saveTourDismissed]);

  const saveTourStep = useCallback((stepIndex) => {
    const stepsSlugs = {
      0: 'step_server_status',
      1: 'step_access_credentials',
      2: 'step_server_SSH',
      3: 'tour_completed',
    };
    tourSession && dispatch(vpsOciActions.tour.saveStep({ step: stepsSlugs[stepIndex], hostingId, session: tourSession }));
  }, [dispatch, hostingId, tourSession]);


  const handleNext = useCallback(() => {
    setCurrentStep((prev) => {
      const totalSteps = locale.guidedTour.steps.length;
      if (prev < totalSteps - 1) {
        return prev + 1;
      }
      handleClose();
      return prev;
    });
  }, [locale.guidedTour.steps.length, handleClose]);

  const createPopoverContent = useCallback((stepIndex) => {
    const stepData = locale.guidedTour.steps[stepIndex];
    const isLastStep = stepIndex === locale.guidedTour.steps.length - 1;

    return (
      <Styles.PopoverContent>
        <Styles.CloseButton onClick={handleClose}>
          <IconClose />
        </Styles.CloseButton>
        <Styles.TextContent>
          <Styles.Title>{stepData.title}</Styles.Title>
          <Styles.Description>{stepData.description}</Styles.Description>
        </Styles.TextContent>
        <Styles.Actions>
          <Styles.StepIndicator>
            {locale.guidedTour.stepIndicator(stepIndex + 1, locale.guidedTour.steps.length)}
          </Styles.StepIndicator>
          <Button
            variant="primary"
            size="large"
            version="v2"
            label={isLastStep ? locale.guidedTour.finishButton : locale.guidedTour.nextButton}
            onClick={handleNext}
            testId={`vps-tour-step-${stepIndex}-button`}
          />
        </Styles.Actions>
      </Styles.PopoverContent>
    );
  }, [locale, handleNext, handleClose]);

  const getElementPosition = useCallback((element) => {
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
      width: rect.width,
      height: rect.height,
      viewportTop: rect.top,
      viewportLeft: rect.left,
    };
  }, []);

  const createSteps = useCallback(() => {
    const newSteps = [];
    const paddings = 12;

    if (myPlanRef) {
      const popoverPaddingTop = 23;
      const popoverTop = (myPlanRef.offsetTop - paddings) + (myPlanRef.offsetHeight + (2 * paddings) + popoverPaddingTop);

      let popoverLeft;
      const popoverMaxWidth = 320;
      if (isMobile) {
        const rect = myPlanRef.getBoundingClientRect();
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const maskLeft = rect.left + scrollLeft - paddings;
        const maskWidth = rect.width + (2 * paddings);
        const maskCenter = maskLeft + (maskWidth / 2);
        popoverLeft = maskCenter - (popoverMaxWidth / 2);
      } else {
        popoverLeft = '35%';
      }

      newSteps.push({
        mask: {
          $left: myPlanRef.offsetLeft - paddings,
          $top: myPlanRef.offsetTop - paddings,
          $width: myPlanRef.offsetWidth + (2 * paddings),
          $height: myPlanRef.offsetHeight + (2 * paddings),
          $customRadius: 16,
          $zIndex: 99999,
        },
        popover: {
          $top: popoverTop,
          $left: popoverLeft,
          $padding: '0',
          $boxSizing: 'content-box',
          $transform: isMobile ? 'unset' : 'translate(-50%, 0%)',
          $height: 'auto',
          $customRadius: 16,
          $zIndex: 999999,
          content: createPopoverContent(0),
          pointer: isTablet || isMobile ? 'topMiddle' : 'topLeft',
          $maxwidth: popoverMaxWidth,
        },
      });
    }

    if (vpsInfoRef) {
      const position = getElementPosition(vpsInfoRef);
      if (position) {
        const popoverPaddingTop = 23;
        const popoverPaddingLeft = 12;
        const popoverWidth = 370;
        const popoverTop = (position.top - paddings) + (position.height + (2 * paddings) + popoverPaddingTop);

        const maskLeft = position.left - paddings;
        const maskWidth = position.width + (2 * paddings);
        const maskCenter = maskLeft + (maskWidth / 2);
        const popoverLeft = isMobile ? maskCenter - (popoverWidth / 2) : position.left + position.width + (paddings * 2) + popoverPaddingLeft;

        newSteps.push({
          mask: {
            $left: maskLeft,
            $top: position.top - paddings,
            $width: maskWidth,
            $height: position.height + (2 * paddings),
            $customRadius: 16,
            $zIndex: 99999,
          },
          popover: {
            $top: isMobile ? popoverTop : popoverTop / 2,
            $left: popoverLeft,
            $padding: '0',
            $boxSizing: 'content-box',
            $height: 'auto',
            $customRadius: 16,
            $zIndex: 999999,
            content: createPopoverContent(1),
            pointer: isMobile ? 'topMiddle' : 'leftTop',
            $maxwidth: popoverWidth,
            scrollOffset: 100,
          },
        });
      }
    }

    if (sshAccessRef) {
      const position = getElementPosition(sshAccessRef);
      if (position) {
        const popoverPaddingTop = 23;
        const popoverTop = (position.top - paddings) + (position.height + (2 * paddings) + popoverPaddingTop);
        const popoverWidth = 320;

        const maskLeft = position.left - paddings;
        const maskWidth = position.width + (2 * paddings);
        const maskCenter = maskLeft + (maskWidth / 2);
        const popoverLeft = isMobile ? maskCenter - (popoverWidth / 2) : position.left - (popoverWidth / 2) - (paddings * 4);

        newSteps.push({
          mask: {
            $left: maskLeft,
            $top: position.top - paddings,
            $width: maskWidth,
            $height: position.height + (2 * paddings),
            $customRadius: 16,
            $zIndex: 99999,
          },
          popover: {
            $top: isMobile ? popoverTop : popoverTop / 2,
            $left: popoverLeft,
            $padding: '0',
            $margin: isMobile ? '0 12px' : '0 0 0 12px',
            $boxSizing: 'content-box',
            $transform: isMobile ? 'unset' : 'translate(-50%, 0%)',
            $height: 'auto',
            $customRadius: 16,
            $zIndex: 999999,
            content: createPopoverContent(2),
            pointer: isMobile ? 'topMiddle' : 'rightTop',
            $maxwidth: popoverWidth,
            scrollOffset: 100,
          },
        });
      }
    }

    if (managementRef) {
      const position = getElementPosition(managementRef);
      if (!position) return newSteps;

      const popoverPaddingTop = 8;
      const popoverWidth = 320;

      const maskTop = position.top - paddings;
      const maskBottom = position.top + position.height + (paddings * 2) + popoverPaddingTop;

      const popoverTop = maskTop - (paddings * 2);
      const popoverTopMobile = maskBottom;

      const maskLeft = position.left - paddings;
      const maskWidth = position.width + (2 * paddings);
      const maskCenter = maskLeft + (maskWidth / 2);
      const popoverLeft = isMobile ? maskCenter - (popoverWidth / 2) : '50%';

      newSteps.push({
        mask: {
          $left: maskLeft,
          $top: maskTop,
          $width: maskWidth,
          $height: position.height + (2 * paddings),
          $customRadius: 16,
          $zIndex: 99999,
        },
        popover: {
          $top: isMobile ? popoverTopMobile : popoverTop,
          $left: popoverLeft,
          $padding: '0',
          $boxSizing: 'content-box',
          $transform: isMobile ? 'unset' : 'translate(-50%, -100%)',
          $height: 'auto',
          $customRadius: 16,
          $zIndex: 999999,
          content: createPopoverContent(3),
          pointer: isMobile ? 'topMiddle' : 'bottomLeft',
          $maxwidth: popoverWidth,
          scrollOffset: 400,
        },
      });
    }

    return newSteps;
  }, [
    myPlanRef,
    vpsInfoRef,
    sshAccessRef,
    managementRef,
    isMobile,
    isTablet,
    createPopoverContent,
    getElementPosition,
  ]);

  const startTour = useCallback(() => {
    if (shouldStart) {
      const newSteps = createSteps();
      if (newSteps.length > 0) {
        setSteps(newSteps);
        setCurrentStep(0);
      }
    }
  }, [shouldStart, createSteps]);

  const updateSteps = useCallback(() => {
    if (steps.length > 0 && currentStep < steps.length) {
      const updatedSteps = createSteps();
      setSteps(updatedSteps);
      setCurrentStep(currentStep);
      saveTourStep(currentStep);
    }
  }, [createSteps, steps.length, currentStep, saveTourStep]);

  const scrollToStep = useCallback(() => {
    if (steps.length > 0 && currentStep < steps.length) {
      const currentStepData = steps[currentStep];
      const { mask, popover } = currentStepData || {};

      if (isMobile && popover && popover.$top) {
        const viewportHeight = window.innerHeight;
        const estimatedPopoverHeight = 270;
        const bottomMargin = 20;
        const scrollTop = popover.$top - viewportHeight + estimatedPopoverHeight + bottomMargin;

        return window.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth',
        });
      }

      if (mask && mask.$top) {
        return window.scrollTo({
          top: Math.max(0, mask.$top - popover.scrollOffset),
          behavior: 'smooth',
        });
      }
    }
  }, [steps, currentStep, isMobile]);

  useEffect(() => {
    startTour();
  }, [startTour]);

  useEffect(() => {
    updateSteps();
  }, [updateSteps]);

  useEffect(() => {
    scrollToStep();
  }, [scrollToStep]);

  if (steps.length === 0 || !shouldStart) {
    return <></>;
  }

  const currentStepData = steps[currentStep];

  if (!currentStepData) {
    return <></>;
  }

  return (
    <Guide
      steps={[currentStepData]}
      testId="vps-guided-tour"
      ignoreClickOutside
      onClose={handleClose}
      $pointerWidth={16}
    />
  );
};

export default VpsGuidedTour;
